import { ShoppingCart } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Ecommerce SEO for online stores (Shopify, WooCommerce, Magento, custom).
 * Scope: organic search growth for a store's own site.
 * Not to be confused with Amazon SEO (marketplace listing optimization).
 */
export const ecommerceSeoPage: MoneyPageContent = {
  id: "ecommerce-seo",
  icon: ShoppingCart,
  serviceType: "Ecommerce SEO",
  esPath: "/servicios/ecommerce-seo",
  enPath: "/en/services/ecommerce-seo",
  es: {
    metaTitle: "Ecommerce SEO: Tráfico Orgánico que Convierte",
    metaDescription:
      "SEO para tiendas online: arquitectura, páginas de categoría y producto, datos estructurados y medición de ingresos orgánicos reales.",
    keywords: [
      "seo ecommerce",
      "seo para tiendas online",
      "seo shopify",
      "seo woocommerce",
      "optimización páginas de producto",
    ],
    h1: "Ecommerce SEO para convertir búsqueda en descubrimiento de productos",
    breadcrumbLabel: "SEO Ecommerce",
    heroSubtitle: "Más tráfico no sirve de nada si no es tráfico comercialmente relevante",
    heroDescription:
      "Una tienda online no crece en Google solo con palabras clave: crece con una arquitectura que Google pueda rastrear e indexar bien, páginas de categoría y producto que respondan a la intención de compra, y control sobre la navegación facetada y el contenido duplicado que suele frenar a los ecommerce más grandes.",
    ctaPrimary: {
      label: "Evaluar el crecimiento orgánico de mi ecommerce",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Arquitectura primero",
        text: "La estructura de categorías y subcategorías determina qué puede rastrear e indexar Google de forma eficiente.",
      },
      {
        label: "Navegación facetada bajo control",
        text: "Filtros y parámetros mal gestionados generan miles de URLs duplicadas que diluyen la autoridad del sitio.",
      },
      {
        label: "Inventario cambiante",
        text: "Productos agotados, descontinuados o de temporada requieren reglas claras, no eliminación automática de páginas con historial de posicionamiento.",
      },
      {
        label: "Ingresos, no solo sesiones",
        text: "El indicador que importa es el ingreso orgánico y las conversiones asistidas, no solo el volumen de visitas.",
      },
    ],
    explanation: {
      title: "Qué es el SEO para ecommerce",
      definition:
        "Es la optimización de una tienda online (Shopify, WooCommerce, Magento o desarrollo a medida) para que sus páginas de categoría y producto aparezcan en resultados de búsqueda ante consultas con intención de compra real, y para que esa visibilidad se traduzca en ingresos medibles, no solo en tráfico.",
      whyImportant: {
        title: "Por qué el ecommerce necesita un enfoque propio",
        text: "Un ecommerce tiene retos que un sitio de contenido no tiene: catálogos que cambian constantemente, navegación facetada que multiplica URLs, páginas de producto casi idénticas entre sí, estacionalidad que hace que ciertas páginas pierdan relevancia varios meses al año, y una búsqueda interna del sitio que revela intención de compra directa si se analiza bien. Tratar un ecommerce con las mismas tácticas que un blog corporativo deja sin resolver justo los problemas que más afectan su visibilidad.",
      },
      consequences: {
        title: "Qué ocurre si no se aborda con ese enfoque",
        text: "Miles de URLs generadas por combinaciones de filtros compitiendo entre sí y con la página de categoría original; páginas de producto con descripciones del fabricante duplicadas en decenas de tiendas; productos agotados devueltos como error 404 en lugar de gestionarse con una estrategia que preserve el valor de posicionamiento acumulado; y reportes que muestran 'más tráfico' mes a mes sin que eso se traduzca en más ingresos, porque el tráfico ganado no tenía intención de compra.",
      },
    },
    problems: {
      title: "Errores comunes en SEO de ecommerce",
      subtitle: "La mayoría se originan en la plataforma, no en el contenido",
      items: [
        {
          icon: ShoppingCart,
          title: "Navegación facetada sin control",
          description:
            "Cada combinación de filtro (color, talla, precio) genera una URL indexable, creando contenido duplicado o casi duplicado a gran escala.",
          impact: "Presupuesto de rastreo desperdiciado y autoridad diluida entre miles de URLs",
        },
        {
          icon: ShoppingCart,
          title: "Páginas de producto con descripciones del fabricante",
          description:
            "El mismo texto que usa el fabricante aparece en decenas de tiendas que venden el mismo producto.",
          impact: "Páginas de producto sin diferenciación que Google no prioriza",
        },
        {
          icon: ShoppingCart,
          title: "Productos agotados o descontinuados mal gestionados",
          description:
            "Se eliminan páginas con historial de posicionamiento en lugar de redirigirlas o mantenerlas con alternativas claras.",
          impact: "Pérdida de rankings y enlaces acumulados en esas URLs",
        },
        {
          icon: ShoppingCart,
          title: "Categorías mal estructuradas",
          description:
            "Jerarquía de categorías que no refleja cómo buscan los usuarios, o categorías con muy pocos productos que compiten débilmente.",
          impact: "Páginas de categoría con bajo potencial de posicionamiento",
        },
        {
          icon: ShoppingCart,
          title: "Medición centrada solo en sesiones",
          description:
            "Los reportes muestran tráfico orgánico creciente sin conectar esas visitas con ingresos, conversiones asistidas o valor del carrito.",
          impact: "Decisiones basadas en volumen de tráfico, no en impacto comercial real",
        },
      ],
    },
    methodology: {
      title: "Cómo se aborda un proyecto de SEO para ecommerce",
      subtitle: "De la arquitectura a los ingresos, en ese orden",
      steps: [
        {
          number: "01",
          title: "Auditoría de arquitectura e indexación",
          description:
            "Revisión de cómo Google rastrea e indexa el catálogo: profundidad de clics, jerarquía de categorías, y volumen de URLs generadas por filtros y parámetros.",
        },
        {
          number: "02",
          title: "Estrategia de navegación facetada",
          description:
            "Definición de qué combinaciones de filtros deben indexarse, cuáles deben canonicalizarse o bloquearse, y cómo evitar duplicidad sin perder capacidad de descubrimiento.",
        },
        {
          number: "03",
          title: "Optimización de páginas de categoría",
          description:
            "Estructura, contenido e intención de búsqueda de las páginas que suelen concentrar más volumen de búsqueda que las páginas de producto individuales.",
        },
        {
          number: "04",
          title: "Optimización de páginas de producto",
          description:
            "Contenido único, datos estructurados de producto (precio, disponibilidad, reseñas) y elementos que refuercen la intención de compra en el fragmento de resultado.",
        },
        {
          number: "05",
          title: "Reglas para inventario cambiante",
          description:
            "Protocolos claros para productos agotados, descontinuados o estacionales: cuándo redirigir, cuándo mantener con alternativas y cuándo sí eliminar.",
        },
        {
          number: "06",
          title: "Búsqueda interna del sitio",
          description:
            "Análisis de las consultas que los usuarios escriben en el buscador interno como fuente directa de intención de compra no cubierta por el catálogo actual.",
        },
        {
          number: "07",
          title: "Medición de ingresos orgánicos",
          description:
            "Configuración de analítica que conecte el tráfico orgánico con ingresos, conversiones asistidas y valor de pedido, no solo con sesiones o clics.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Auditoría de arquitectura, rastreo e indexación del catálogo",
          "Estrategia de navegación facetada y gestión de parámetros de URL",
          "Optimización de páginas de categoría y producto",
          "Datos estructurados de producto (Product, Offer, Review)",
          "Reglas de gestión para productos agotados, descontinuados y estacionales",
          "Análisis de búsqueda interna del sitio como señal de intención",
          "Medición de ingresos orgánicos y conversiones asistidas",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Optimización de listados en Amazon u otros marketplaces (ver Amazon SEO)",
          "Gestión de campañas de Google Shopping o publicidad paga",
          "Diseño de UX/UI del checkout o del carrito",
          "Desarrollo o migración de plataforma ecommerce",
          "Gestión de inventario o logística",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién tiene sentido",
        items: [
          "Tiendas online en Shopify, WooCommerce, Magento u otra plataforma con catálogo activo",
          "Ecommerce con cientos o miles de productos y problemas de indexación",
          "Marcas que ven tráfico orgánico estable pero ingresos que no crecen al mismo ritmo",
          "Negocios que compiten en categorías con productos muy similares entre tiendas",
          "Ecommerce con catálogo estacional o alta rotación de inventario",
        ],
      },
      notFor: {
        title: "Para quién no es el siguiente paso",
        items: [
          "Vendedores que operan exclusivamente en marketplaces sin sitio propio",
          "Tiendas recién lanzadas sin catálogo ni historial que auditar",
          "Negocios que buscan solo diseño o desarrollo, sin componente de búsqueda orgánica",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿El SEO de ecommerce es distinto del SEO de un sitio normal?",
          answer:
            "Sí, en varios aspectos clave: volumen de páginas, navegación facetada, inventario que cambia constantemente y descripciones de producto que se repiten entre competidores. Requiere decisiones técnicas específicas para catálogos grandes.",
        },
        {
          question: "¿Qué hago con los productos que ya no tengo en stock?",
          answer:
            "Depende del caso: si es temporal, se mantiene la página con alternativas y aviso claro; si es definitivo, se evalúa redirigir a una categoría o producto relevante para no perder el valor de posicionamiento acumulado. Eliminar la página sin más suele ser el peor escenario.",
        },
        {
          question: "¿Debo indexar todas las páginas generadas por los filtros de mi tienda?",
          answer:
            "En la mayoría de los casos, no. Solo las combinaciones con volumen de búsqueda real y demanda propia deberían indexarse; el resto debe gestionarse con canonicals, robots o parámetros para evitar duplicidad.",
        },
        {
          question: "¿Cómo se mide si el SEO de ecommerce está funcionando?",
          answer:
            "El indicador principal es el ingreso orgánico y las conversiones asistidas por tráfico orgánico, no solo las sesiones. Un aumento de tráfico sin aumento de ingresos suele indicar que se está atrayendo tráfico de baja intención comercial.",
        },
        {
          question: "¿Esto sirve también si vendo en Amazon?",
          answer:
            "Este servicio se enfoca en tu tienda propia. Si además vendes en Amazon, esa es una disciplina distinta con sus propias reglas de posicionamiento dentro del marketplace.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "Para complementar la estrategia de ecommerce",
      links: [
        {
          label: "Consultoría de Amazon",
          description: "Si además vendes en Amazon, la optimización de listados sigue reglas distintas.",
          to: "/servicios/amazon-consulting",
        },
        {
          label: "SEO técnico",
          description: "Base técnica necesaria para que un catálogo grande se rastree e indexe bien.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "Posicionamiento en IA",
          description: "Cómo aparecen tus productos en respuestas de buscadores con IA y asistentes.",
          to: "/servicios/posicionamiento-llm",
        },
        {
          label: "Diagnóstico SEO profesional",
          description: "Auditoría inicial para identificar qué está frenando el crecimiento orgánico.",
          to: "/servicios/seo-diagnostico",
        },
        {
          label: "SEO internacional",
          description: "Si tu tienda vende o planea vender en otros países.",
          to: "/servicios/seo-internacional",
        },
      ],
    },
    llmSummary:
      "Servicio de SEO para tiendas online centrado en arquitectura de catálogo, páginas de categoría y producto, gestión de navegación facetada e inventario cambiante, y medición de ingresos orgánicos reales frente a solo tráfico.",
    keyPoints: [
      "La arquitectura del catálogo determina qué puede indexar Google eficientemente",
      "La navegación facetada sin control genera contenido duplicado a gran escala",
      "Los productos agotados o descontinuados requieren reglas, no eliminación automática",
      "El indicador clave es el ingreso orgánico y las conversiones asistidas, no solo el tráfico",
      "Distinto de Amazon SEO, que optimiza listados dentro de un marketplace",
    ],
  },
  en: {
    metaTitle: "Ecommerce SEO: Organic Traffic That Converts",
    metaDescription:
      "SEO for online stores: architecture, category and product pages, structured data, and measuring real organic revenue.",
    keywords: [
      "ecommerce seo",
      "seo for online stores",
      "shopify seo",
      "woocommerce seo",
      "product page optimization",
    ],
    h1: "Ecommerce SEO That Turns Search Into Product Discovery",
    breadcrumbLabel: "Ecommerce SEO",
    heroSubtitle: "More traffic is worthless if it isn't commercially relevant traffic",
    heroDescription:
      "An online store doesn't grow in Google with keywords alone: it grows with an architecture Google can crawl and index efficiently, category and product pages built around purchase intent, and control over the faceted navigation and duplicate content that tend to slow down larger stores.",
    ctaPrimary: {
      label: "Evaluate my store's organic growth",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See documented work", to: "/en/case-studies" },
    summary: [
      {
        label: "Architecture first",
        text: "Category and subcategory structure determines what Google can efficiently crawl and index.",
      },
      {
        label: "Faceted navigation under control",
        text: "Poorly managed filters and parameters generate thousands of duplicate URLs that dilute site authority.",
      },
      {
        label: "Shifting inventory",
        text: "Out-of-stock, discontinued, and seasonal products need clear rules, not automatic deletion of pages with ranking history.",
      },
      {
        label: "Revenue, not just sessions",
        text: "The metric that matters is organic revenue and assisted conversions, not just traffic volume.",
      },
    ],
    explanation: {
      title: "What ecommerce SEO means",
      definition:
        "It's the optimization of an online store (Shopify, WooCommerce, Magento, or custom-built) so its category and product pages show up for searches with real purchase intent, and so that visibility translates into measurable revenue, not just traffic.",
      whyImportant: {
        title: "Why ecommerce needs its own approach",
        text: "An ecommerce site faces challenges a content site doesn't: catalogs that change constantly, faceted navigation that multiplies URLs, near-identical product pages, seasonality that makes certain pages irrelevant for months at a time, and on-site search behavior that reveals direct purchase intent when analyzed properly. Treating an ecommerce site with the same tactics as a corporate blog leaves unresolved exactly the problems hurting its visibility the most.",
      },
      consequences: {
        title: "What happens without that approach",
        text: "Thousands of URLs generated by filter combinations competing against each other and against the original category page; product pages using manufacturer descriptions duplicated across dozens of stores; out-of-stock products turned into 404 errors instead of managed with a strategy that preserves accumulated ranking value; and reports showing 'more traffic' month over month without it translating into more revenue, because the traffic gained had no purchase intent.",
      },
    },
    problems: {
      title: "Common ecommerce SEO mistakes",
      subtitle: "Most originate in the platform, not the content",
      items: [
        {
          icon: ShoppingCart,
          title: "Uncontrolled faceted navigation",
          description:
            "Every filter combination (color, size, price) generates an indexable URL, creating duplicate or near-duplicate content at scale.",
          impact: "Wasted crawl budget and authority diluted across thousands of URLs",
        },
        {
          icon: ShoppingCart,
          title: "Product pages using manufacturer descriptions",
          description:
            "The same text the manufacturer provides appears on dozens of stores selling the same product.",
          impact: "Undifferentiated product pages Google doesn't prioritize",
        },
        {
          icon: ShoppingCart,
          title: "Poorly managed out-of-stock or discontinued products",
          description:
            "Pages with ranking history get deleted instead of redirected or kept with clear alternatives.",
          impact: "Lost rankings and accumulated links on those URLs",
        },
        {
          icon: ShoppingCart,
          title: "Poorly structured categories",
          description:
            "A category hierarchy that doesn't match how users actually search, or categories with too few products to compete meaningfully.",
          impact: "Category pages with low ranking potential",
        },
        {
          icon: ShoppingCart,
          title: "Measurement focused only on sessions",
          description:
            "Reports show growing organic traffic without connecting those visits to revenue, assisted conversions, or cart value.",
          impact: "Decisions based on traffic volume, not actual commercial impact",
        },
      ],
    },
    methodology: {
      title: "How an ecommerce SEO project is approached",
      subtitle: "From architecture to revenue, in that order",
      steps: [
        {
          number: "01",
          title: "Architecture and indexation audit",
          description:
            "Review of how Google crawls and indexes the catalog: click depth, category hierarchy, and the volume of URLs generated by filters and parameters.",
        },
        {
          number: "02",
          title: "Faceted navigation strategy",
          description:
            "Defining which filter combinations should be indexed, which should be canonicalized or blocked, and how to avoid duplication without losing discoverability.",
        },
        {
          number: "03",
          title: "Category page optimization",
          description:
            "Structure, content, and search intent for the pages that usually capture more search volume than individual product pages.",
        },
        {
          number: "04",
          title: "Product page optimization",
          description:
            "Unique content, product structured data (price, availability, reviews), and elements that reinforce purchase intent in the search snippet.",
        },
        {
          number: "05",
          title: "Rules for shifting inventory",
          description:
            "Clear protocols for out-of-stock, discontinued, and seasonal products: when to redirect, when to keep with alternatives, and when deletion is actually right.",
        },
        {
          number: "06",
          title: "On-site search analysis",
          description:
            "Reviewing what users type into the internal search bar as a direct source of purchase intent not yet covered by the current catalog.",
        },
        {
          number: "07",
          title: "Organic revenue measurement",
          description:
            "Setting up analytics that connect organic traffic to revenue, assisted conversions, and order value — not just sessions or clicks.",
        },
      ],
      ctaText: "Request a Strategic SEO Review",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What this service includes",
        items: [
          "Catalog crawl and indexation architecture audit",
          "Faceted navigation strategy and URL parameter management",
          "Category and product page optimization",
          "Product structured data (Product, Offer, Review)",
          "Management rules for out-of-stock, discontinued, and seasonal products",
          "On-site search analysis as an intent signal",
          "Organic revenue and assisted conversion measurement",
        ],
      },
      notIncludes: {
        title: "What it doesn't include",
        items: [
          "Amazon or other marketplace listing optimization (see Amazon SEO)",
          "Google Shopping campaign or paid ads management",
          "Checkout or cart UX/UI design",
          "Ecommerce platform development or migration",
          "Inventory management or logistics",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this makes sense for",
        items: [
          "Online stores on Shopify, WooCommerce, Magento, or another platform with an active catalog",
          "Ecommerce sites with hundreds or thousands of products and indexation issues",
          "Brands seeing stable organic traffic but revenue that isn't keeping pace",
          "Businesses competing in categories with near-identical products across stores",
          "Ecommerce sites with seasonal catalogs or high inventory turnover",
        ],
      },
      notFor: {
        title: "Who should look elsewhere first",
        items: [
          "Sellers operating exclusively on marketplaces without their own site",
          "Newly launched stores with no catalog or history to audit",
          "Businesses looking only for design or development, with no organic search component",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "Is ecommerce SEO different from regular site SEO?",
          answer:
            "Yes, in several key ways: page volume, faceted navigation, constantly shifting inventory, and product descriptions repeated across competitors. It requires specific technical decisions for large catalogs.",
        },
        {
          question: "What should I do with products that are out of stock?",
          answer:
            "It depends: if it's temporary, the page stays up with alternatives and a clear notice; if it's permanent, redirecting to a relevant category or product is usually considered to preserve accumulated ranking value. Simply deleting the page is usually the worst option.",
        },
        {
          question: "Should I index every page generated by my store's filters?",
          answer:
            "In most cases, no. Only combinations with real, independent search demand should be indexed; the rest should be handled with canonicals, robots directives, or parameter handling to avoid duplication.",
        },
        {
          question: "How is ecommerce SEO success measured?",
          answer:
            "The main metric is organic revenue and conversions assisted by organic traffic, not just sessions. Traffic growth without revenue growth usually signals low-intent traffic is being attracted.",
        },
        {
          question: "Does this also work if I sell on Amazon?",
          answer:
            "This service focuses on your own store. If you also sell on Amazon, that's a separate discipline with its own ranking rules inside the marketplace.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "To complement your ecommerce strategy",
      links: [
        {
          label: "Amazon consulting",
          description: "If you also sell on Amazon, listing optimization follows different rules.",
          to: "/en/services/amazon-consulting",
        },
        {
          label: "Technical SEO",
          description: "The technical foundation a large catalog needs to crawl and index well.",
          to: "/en/services/technical-seo",
        },
        {
          label: "AI Search positioning",
          description: "How your products show up in AI-powered search and assistant answers.",
          to: "/en/services/llm-positioning",
        },
        {
          label: "Professional SEO audit",
          description: "An initial audit to identify what's holding back organic growth.",
          to: "/en/services/seo-audit",
        },
        {
          label: "International SEO",
          description: "If your store sells, or plans to sell, in other countries.",
          to: "/en/services/international-seo",
        },
      ],
    },
    llmSummary:
      "SEO service for online stores focused on catalog architecture, category and product pages, managing faceted navigation and shifting inventory, and measuring real organic revenue rather than just traffic.",
    keyPoints: [
      "Catalog architecture determines what Google can efficiently index",
      "Uncontrolled faceted navigation generates duplicate content at scale",
      "Out-of-stock or discontinued products need rules, not automatic deletion",
      "The key metric is organic revenue and assisted conversions, not just traffic",
      "Distinct from Amazon SEO, which optimizes listings within a marketplace",
    ],
  },
};
